import React, {useEffect, useState} from 'react';
import {Column} from "primereact/column";
import {Chip} from "primereact/chip";
import {Button} from "primereact/button";
import {DataTable} from "primereact/datatable";
import {Card} from "primereact/card";
import useApi from "../../hooks/useApi";
import {webInfo} from "../../utils/webInfo";
import {Link, useNavigate} from "react-router-dom";
import {Paginator} from "primereact/paginator";
import TableSkeleton from "../../Loader/Loader";
import ErrorPresentor from "../../ErrorPresentor/ErrorPresentor";
import {InputSwitch} from "primereact/inputswitch";
import {catchError} from "../../utils/helper";
import usePost from "../../hooks/usePost";
import useDelete from "../../hooks/useDelete";
import {Dialog} from "primereact/dialog";
import {Skeleton} from "primereact/skeleton";
import EmptyData from "../../components/EmptyData";
import moment from "moment/moment";

const Career = () => {
    const navigate = useNavigate();
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage] = useState(10);
    const {get, loading, error} = useApi<any>();
    const {post, loading: postLoading} = usePost();
    const {remove} = useDelete()
    const [update, setUpdate] = useState(false);
    const url = `${webInfo.baseUrl}/api/admin/careers?page=${page}&per_page=${perPage}`
    const [careerPosts, setCareerPosts] = useState([]);
    const [visible, setVisible] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const onPageChange = (event: any) => {
        setPage(event.page + 1);
    }

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const res: any = await get(url);
                setCareerPosts(res.data.data);
                setTotal(res.data.total);
            } catch (err) {
                console.error("Failed to fetch products", err);
            }
        };
        fetchBlogs();
        // eslint-disable-next-line
    }, [page, perPage, update]);

    // Render image
    const imageTemplate = (rowData: any) => (
        <img
            src={`${webInfo.baseUrl}/${rowData.image}`}
            alt={rowData.title}
            style={{width: "50px", height: "50px", objectFit: "cover", borderRadius: "6px"}}
        />
    );

    // Render title with chips below
    const titleTemplate = (rowData: any) => {
        const salaryRange = (rowData.salary_from && rowData.salary_to) ? `${rowData.salary_from} ${rowData.salary_to}` : 'Negotiable'
        return <div>
            <Link className="text-gray-900 underline" to={`/career/${rowData.id}`}
                  style={{fontWeight: 600}}>{rowData.title}</Link>
            <div className="flex flex-wrap gap-2 mt-1">
                <Chip label={salaryRange} className="p-mr-2"/>
                <Chip label={`Vacancies: ${rowData.number_of_vacancy}`} className="p-mr-2"/>
                <Chip label={rowData.job_type} className="p-mr-2"/>
            </div>
        </div>
    };

    // Render actions
    const publishStatus = (rowData: any) => {
        const statusUpdateUrl = `${webInfo.baseUrl}/api/admin/careers/${rowData.id}/publish-status`
        const updateStatus = async (rowData: any) => {
            const payload = {
                status: Number(rowData.status) === 1 ? 0 : 1,
            }
            try {
                const res: any = await post(statusUpdateUrl, true, payload)
                if (res.success) {
                    setUpdate(!update);
                }
            } catch (e) {
                catchError(e)
            }
        }

        if (postLoading) {
            return <Skeleton width={"4rem"} height={"2.5rem"}></Skeleton>
        }

        return <div className="flex gap-2 justify-content-start">
            <InputSwitch checked={Number(rowData?.status) === 1} onChange={() => updateStatus(rowData)}/>
        </div>
    };

    const deleteItem = (rowData: any) => {
        return <div className='flex gap-2'>
            <Button badge={rowData?.apply_count} badgeClassName="p-badge-primary"
                    onClick={() => navigate(`/career/inbox/${rowData.id}`)} title={'Inbox'} icon={'pi pi-inbox'}
                    />
            <Button onClick={() => {
                setVisible(true)
                setSelectedId(rowData.id)
            }} title={'Delete'} icon={'pi pi-trash'} severity={'danger'}/>
        </div>
    }

    const deleteCareer = async () => {
        const deleteUrl = `${webInfo.baseUrl}/api/admin/careers/${selectedId}`;
        try {
            const res: any = await remove(deleteUrl)
            if (res.success) {
                setUpdate(!update);
                setVisible(false)
            }
        } catch (e) {
            catchError(e)
        }
    }

    const headerElement = (
        <div className="inline-flex align-items-center justify-content-center gap-2">
            <span className="font-bold white-space-nowrap">Remove career confirmation</span>
        </div>
    );

    const footerContent = (
        <div className="flex justify-content-end align-items-center">
            <Button label="cancel" icon="pi pi-times" onClick={() => setVisible(false)} autoFocus/>
            <Button label="Delete" severity={'danger'} className="ml-2" icon="pi pi-delete" onClick={deleteCareer}
                    autoFocus/>
        </div>
    );

    const companyTemplate = (data: any) => {
        return <span>{data?.company?.name || 'N/A'}</span>
    }
    const departmentTemplate = (data: any) => {
        return <span>{data?.department?.name || 'N/A'}</span>
    }
    const dateTemplate = (data: any) => {
        return <span>{moment(data.created_at).format("LL")}</span>
    }

    const dataTable = (
        <div>
            <Dialog visible={visible} modal header={headerElement} footer={footerContent} style={{width: '30rem'}}
                    onHide={() => {
                        if (!visible) return;
                        setVisible(false);
                    }}>
                <p className='m-0'>Are you sure, deleting this item? Once you delete it won't be able to view again</p>
            </Dialog>
            <div>
                {(careerPosts?.length > 0 && !loading) ? <div>
                        <DataTable
                            value={careerPosts}
                            rows={5}
                            stripedRows
                        >
                            <Column header="Date" body={dateTemplate}/>
                            <Column header="Image" body={imageTemplate} style={{width: "100px", textAlign: "center"}}/>
                            <Column header="Title & Info" body={titleTemplate}/>
                            <Column body={companyTemplate} header="Company" sortable/>
                            <Column body={departmentTemplate} header="Department" sortable/>
                            <Column header="Publish Status" body={publishStatus}
                                    style={{width: "180px", textAlign: "center"}}/>
                            <Column header="Action" body={deleteItem} style={{width: "180px", textAlign: "center"}}/>
                        </DataTable>
                        <Paginator
                            first={(page - 1) * perPage}
                            rows={perPage}
                            totalRecords={total}
                            rowsPerPageOptions={[10, 20, 30]}
                            onPageChange={onPageChange}
                        />
                    </div> :
                    <EmptyData/>}
            </div>
        </div>
    )

    const header = (
        <div className='flex align-items-center justify-content-between p-4 pb-0'>
            <h4 className="m-0">Career</h4>
            <div>
                <Button onClick={() => navigate("/career/create-new")} label="Create New" icon="pi pi-plus"
                        iconPos="right"/>
            </div>
        </div>
    )

    return (
        <Card header={header}>
            {loading ? <TableSkeleton/> : <div>
                {error ? <ErrorPresentor error={error}/> : dataTable}
            </div>}
        </Card>
    );

};

export default Career;